import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";

export interface ThumbnailCue {
  startMs: number;
  endMs: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

const TIME_REGEX = /^(\d{2}):(\d{2}):(\d{2})\.(\d{3})/;
const XYWH_REGEX = /#xywh=(\d+),(\d+),(\d+),(\d+)/;

function parseVttTime(match: RegExpMatchArray): number {
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3], 10);
  const ms = parseInt(match[4], 10);
  return hours * 3600000 + minutes * 60000 + seconds * 1000 + ms;
}

@Injectable()
export class TimelineThumbnailVttService {
  private cache = new Map<string, Promise<ThumbnailCue[]>>();

  constructor(private http: HttpClient) {}

  getCues(vttUrl: string): Promise<ThumbnailCue[]> {
    if (!vttUrl?.trim()) {
      return Promise.resolve([]);
    }
    let promise = this.cache.get(vttUrl);
    if (!promise) {
      promise = this.fetchAndParse(vttUrl);
      this.cache.set(vttUrl, promise);
    }
    return promise;
  }

  private fetchAndParse(vttUrl: string): Promise<ThumbnailCue[]> {
    return this.http
      .get(vttUrl, { responseType: "text" })
      .toPromise()
      .then((text) => this.parseVtt(text ?? ""))
      .catch(() => []);
  }

  private parseVtt(text: string): ThumbnailCue[] {
    const cues: ThumbnailCue[] = [];
    const blocks = text.split(/\n\s*\n/).filter((b) => b.trim());

    for (const block of blocks) {
      const lines = block.trim().split(/\n/);
      const timeLine = lines.find((l) => l.includes("-->"));
      if (!timeLine) continue;

      const [startStr, endStr] = timeLine.split("-->").map((s) => s.trim());
      const startPart = startStr.match(TIME_REGEX);
      const endPart = endStr.match(TIME_REGEX);
      if (!startPart || !endPart) continue;

      const startMs = parseVttTime(startPart);
      const endMs = parseVttTime(endPart);

      const payloadLine = lines.slice(1).find((l) => l.trim());
      const xywhMatch = payloadLine?.trim().match(XYWH_REGEX);
      if (!xywhMatch) continue;

      const x = parseInt(xywhMatch[1], 10);
      const y = parseInt(xywhMatch[2], 10);
      const w = parseInt(xywhMatch[3], 10);
      const h = parseInt(xywhMatch[4], 10);

      cues.push({ startMs, endMs, x, y, w, h });
    }

    cues.sort((a, b) => a.startMs - b.startMs);
    return cues;
  }
}
